import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { withRouter } from 'app/helpers/routing';
import { bindAll } from '@bibliocommons/utils-react';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { AlertInfo, AlertSuccess, AlertWarning } from '@bibliocommons/deprecated-base-alerts';
import { SecondaryButton } from '@bibliocommons/deprecated-base-buttons';
import { userIdentityShape, routerShape } from '@bibliocommons/bc-prop-types';

import HelpLinkContainer from 'app/components/shared/HelpLink/HelpLinkContainer';
import { IMPORT_STATUSES, SHELF_NAMES, SHELF_IMPORT_NOTICE_ID } from 'app/constants/ShelfConstants';
import accountShape from 'app/shapes/accountShape';

import './ShelfImportAlert.scss';

const helpLink = (
  <HelpLinkContainer page="045faq/070faq-for-later/#listimport" showIcon={false}>
    <ScreenReaderMessage>
      <FormattedMessage id="ada_shelves_import_learn_more" />
    </ScreenReaderMessage>
    <span aria-hidden>
      <FormattedMessage id="learn_more" />
    </span>
  </HelpLinkContainer>
);

export class ShelfImportAlert extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      resultsDismissed: false
    };
  }

  componentDidUpdate(prevProps) {
    const nextImportResult = this.props.importResult;
    if (prevProps.importResult !== nextImportResult && nextImportResult.get('status') === IMPORT_STATUSES.DONE) {
      const { router, shelfName, shelfOwner } = this.props;
      this.props.shelfActions.searchUserShelf(shelfName, shelfOwner.get('id'), router.query);
    }
  }

  importILSBibs() {
    const { currentAccount, shelfName, shelfActions } = this.props;
    shelfActions.importILSBibs(shelfName, currentAccount.get('id'));
  }

  dismissNotice() {
    this.props.noticeActions.dismissNotice(SHELF_IMPORT_NOTICE_ID);
  }

  dismissResults() {
    this.setState({ resultsDismissed: true });
  }

  renderResults() {
    if (this.state.resultsDismissed) {
      return null;
    }

    const { importResult } = this.props;

    switch (importResult.get('status')) {
      case IMPORT_STATUSES.DONE: {
        const count = importResult.get('count') || 0;

        if (count > 0) {
          return (
            <AlertSuccess handleDismiss={this.dismissResults}>
              <FormattedMessage id="message_successful_shelf_import" values={{ count }} />
            </AlertSuccess>
          );
        }

        return (
          <AlertWarning handleDismiss={this.dismissResults}>
            <FormattedMessage id="message_no_items_to_import" />
          </AlertWarning>
        );
      }

      case IMPORT_STATUSES.SUBMITTED: {
        return (
          <AlertSuccess handleDismiss={this.dismissResults}>
            <FormattedMessage id="message_shelf_import_submitted" />
          </AlertSuccess>
        );
      }

      default: {
        return null;
      }
    }
  }

  renderNotice() {
    const { importResult, localBranch } = this.props;
    const handleDismiss = localBranch ? undefined : this.dismissNotice;

    return (
      <AlertInfo handleDismiss={handleDismiss}>
        <span className="alert-message">
          <FormattedMessage id="message_shelf_import" />
        </span>{' '}
        {helpLink}
        <div className="alert-actions">
          <SecondaryButton
            className="shelf-import-button"
            dataKey="import-list-button"
            handleClick={this.importILSBibs}
            isLoading={importResult.get('isFetching')}
          >
            <FormattedMessage id="button_shelf_import" />
          </SecondaryButton>
        </div>
      </AlertInfo>
    );
  }

  render() {
    const { shelfName, shelfOwner, currentAccount, currentLibrary, importResult, hiddenNotices } = this.props;

    if (
      shelfOwner.get('loggedIn') &&
      currentAccount &&
      currentLibrary.get('listImport') &&
      shelfName === SHELF_NAMES.FOR_LATER
    ) {
      const importedILSShelves = currentAccount.get('importedILSShelves');
      const isHidden = hiddenNotices.has(SHELF_IMPORT_NOTICE_ID);

      if (!importedILSShelves && !isHidden) {
        return (
          <div className="cp-shelf-import-alert">
            {importResult.get('status') ? this.renderResults() : this.renderNotice()}
          </div>
        );
      }
    }

    return null;
  }
}

const importResultShape = ImmutablePropTypes.mapContains({
  count: PropTypes.number,
  status: PropTypes.string,
  isFetching: PropTypes.bool
});

ShelfImportAlert.propTypes = {
  shelfName: PropTypes.string.isRequired,
  shelfOwner: userIdentityShape.isRequired,
  currentAccount: accountShape,
  importResult: importResultShape,
  localBranch: PropTypes.string,
  hiddenNotices: ImmutablePropTypes.set.isRequired,
  noticeActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentLibrary: ImmutablePropTypes.map.isRequired,
  router: routerShape.isRequired
};

export default withRouter(ShelfImportAlert);
